import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';
import { useBranchFilter } from '../../../contexts/branch-filter-context';
import { useSelectedBranch } from '../../../contexts/selected-branch-context';
import { Store, AccountTree } from '@mui/icons-material';

/**
 * Global branch filter selector that appears under page titles.
 * Allows users to switch between viewing current branch only or all branches.
 * State persists across page navigation.
 * 
 * Custom modern segmented control design.
 */
export const BranchFilterSelector: React.FC = () => {
  const { filterMode, setFilterMode } = useBranchFilter();
  const { selectedBranch } = useSelectedBranch();
  const theme = useTheme();

  // Don't show if no branch is selected
  if (!selectedBranch) {
    return null;
  }

  const activeColor = theme.palette.primary.main;
  const inactiveColor = theme.palette.text.secondary;
  const bgColor = alpha(theme.palette.primary.main, 0.08);

  const currentBranchRef = React.useRef<HTMLDivElement>(null);
  const allBranchesRef = React.useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = React.useState({ left: 3, width: 0 });

  React.useEffect(() => {
    if (filterMode === 'current' && currentBranchRef.current) {
      setIndicatorStyle({
        left: 3,
        width: currentBranchRef.current.offsetWidth,
      });
    } else if (filterMode === 'all' && allBranchesRef.current && currentBranchRef.current) {
      setIndicatorStyle({
        left: currentBranchRef.current.offsetWidth + 3,
        width: allBranchesRef.current.offsetWidth,
      });
    }
  }, [filterMode, selectedBranch]);

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        mb: 3,
        mt: 1
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          position: 'relative',
          backgroundColor: bgColor,
          borderRadius: '50px',
          padding: '3px',
          boxShadow: `inset 0 1px 3px ${alpha(theme.palette.common.black, 0.12)}`,
        }}
      >
        {/* Sliding background indicator */}
        <Box
          sx={{
            position: 'absolute',
            top: '3px',
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`,
            height: 'calc(100% - 6px)',
            backgroundColor: activeColor,
            borderRadius: '50px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: `0 2px 6px ${alpha(activeColor, 0.35)}`,
            zIndex: 0,
          }}
        />

        {/* Current Branch Option */}
        <Box
          ref={currentBranchRef}
          onClick={() => setFilterMode('current')}
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            padding: '7px 18px',
            minWidth: 'fit-content',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.2s ease',
            borderRadius: '50px',
            '&:hover': {
              transform: filterMode !== 'current' ? 'scale(1.02)' : 'none',
            },
          }}
        >
          <Store
            sx={{
              fontSize: '18px',
              color: filterMode === 'current' ? theme.palette.primary.contrastText : inactiveColor,
              transition: 'color 0.2s ease',
            }}
          />
          <Typography
            sx={{
              fontWeight: filterMode === 'current' ? 600 : 500,
              fontSize: '0.875rem',
              color: filterMode === 'current' ? theme.palette.primary.contrastText : inactiveColor,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedBranch.name}
          </Typography>
        </Box>

        {/* All Branches Option */}
        <Box
          ref={allBranchesRef}
          onClick={() => setFilterMode('all')}
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            padding: '7px 18px',
            minWidth: 'fit-content',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.2s ease',
            borderRadius: '50px',
            '&:hover': {
              transform: filterMode !== 'all' ? 'scale(1.02)' : 'none',
            },
          }}
        >
          <AccountTree
            sx={{
              fontSize: '18px',
              color: filterMode === 'all' ? theme.palette.primary.contrastText : inactiveColor,
              transition: 'color 0.2s ease',
            }}
          />
          <Typography
            sx={{
              fontWeight: filterMode === 'all' ? 600 : 500,
              fontSize: '0.875rem',
              color: filterMode === 'all' ? theme.palette.primary.contrastText : inactiveColor,
              transition: 'all 0.2s ease',
              whiteSpace: 'nowrap',
            }}
          >
            All Branches
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default BranchFilterSelector;

